import { getAllClauses, getClauseById, IClause, IClauseList } from "@/api";

import { UserChip } from "@/components/shared/chips/UserChip";
import { Table } from "@/components/shared/table/Table";
import { ClauseTypesCell } from "@/components/shared/table/cells/ClauseTypesCell";
import { PolicyTypeCell } from "@/components/shared/table/cells/PolicyTypeCell";
import { useAuth } from "@/contexts/auth/useAuth";
import { useOrganization } from "@/contexts/frontend/organization/useOrganization";
import { useFilterModel } from "@/hooks/useFilterModel";
import { useMediaQuery, useTheme } from "@mui/material";
import { GridColDef, GridListViewColDef } from "@mui/x-data-grid-pro";
import { useQuery } from "@tanstack/react-query";
import { useDialogs } from "@toolpad/core/useDialogs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { ClauseLibraryTableHeader } from "./ClauseLibraryTableHeader";
import { ClauseLibraryTableToolbar } from "./ClauseLibraryTableToolbar";
import { ClauseListViewCell } from "./cells/ClauseListViewCell";
import { ClauseTitleCell } from "./cells/ClauseTitleCell";
import { ClauseDetailsDialog } from "./dialogs/clause-details-dialog/ClauseDetailsDialog";

// Table columns
const columns: GridColDef<IClauseList>[] = [
    {
        field: "title",
        headerName: "Clause Title",
        maxWidth: 360,
        renderCell: ({ row }) => <ClauseTitleCell clause={row} />,
    },
    {
        field: "created_by",
        headerName: "Created By",
        sortable: false,
        renderCell: () => <UserChip insureScope size="small" />,
    },
    {
        field: "clause_types",
        headerName: "Clause Types",
        maxWidth: 220,
        sortable: false,
        renderCell: ({ row }) => (
            <ClauseTypesCell clauseTypes={row.clause_types} />
        ),
    },
    {
        field: "policy_type",
        headerName: "Policy Type",
        sortable: false,
        renderCell: ({ row }) => (
            <PolicyTypeCell policyType={row.policy_type} />
        ),
    },
];

/**
 * The Clause Library table
 */
export const ClauseLibraryTable = () => {
    const { organization } = useOrganization();
    const { user } = useAuth();
    const theme = useTheme();
    const isListView = useMediaQuery(theme.breakpoints.down("md"));
    const dialogs = useDialogs();

    const [searchParams, setSearchParams] = useSearchParams();
    const clauseId = searchParams.get("clauseId");

    const [rows, setRows] = useState<IClauseList[]>([]);

    const { filterModel, handleFilterModelChange } = useFilterModel();

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    const autoSizeOptions = {
        columns: ["title", "policy_type", "clause_types", "created_by"],
        includeHeaders: true,
        includeOutliers: true,
        expand: true,
    };

    // Fetch clauses from the API
    const { data, isFetching } = useQuery({
        queryKey: [
            "frontend-organization-clause-library",
            organization.id,
            paginationModel.page,
            paginationModel.pageSize,
            filterModel,
        ],
        queryFn: () =>
            getAllClauses(
                paginationModel.page + 1,
                paginationModel.pageSize,
                filterModel
            ),
    });

    // Separate query for individual clause when clauseId is present
    const { data: selectedClause } = useQuery({
        queryKey: ["frontend-clause", clauseId],
        queryFn: () => getClauseById(Number(clauseId)),
        enabled: !!clauseId && !isNaN(Number(clauseId)),
        retry: 1, // Don't retry too much for invalid IDs
    });

    useEffect(() => {
        if (data) {
            setRows(data.items);
        }
    }, [data]);

    // Handle dialog close
    const handleDialogClose = useCallback(() => {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete("clauseId");
        setSearchParams(newSearchParams);
        // Reset the opened dialog tracker when dialog is closed
        openedDialogForClauseIdRef.current = null;
    }, [searchParams, setSearchParams]);

    const handleOpenDialog = useCallback(
        (clause: IClauseList) => {
            dialogs
                .open(ClauseDetailsDialog, {
                    clause,
                    user,
                })
                .then(() => {
                    handleDialogClose();
                });
        },
        [dialogs, handleDialogClose, user]
    );

    // Reset dialog tracker when clauseId is removed from URL
    useEffect(() => {
        if (!clauseId) {
            openedDialogForClauseIdRef.current = null;
        }
    }, [clauseId]);

    // Handle opening dialog when clauseId is present and clause is loaded
    useEffect(() => {
        if (!clauseId || !selectedClause) return;

        // Prevent opening multiple dialogs for the same clauseId
        if (openedDialogForClauseIdRef.current === clauseId) return;

        openedDialogForClauseIdRef.current = clauseId;
        handleOpenDialog(selectedClause);
    }, [clauseId, handleOpenDialog, selectedClause]);

    // memoize rowCount to avoid resetting the page to 0 when the data is loading
    const rowCountRef = useRef(data?.total || 0);

    // Track which clauseId we've already opened a dialog for to prevent infinite dialogs
    const openedDialogForClauseIdRef = useRef<string | null>(null);

    const rowCount = useMemo(() => {
        if (data?.total !== undefined) {
            rowCountRef.current = data?.total;
        }
        return rowCountRef.current;
    }, [data?.total]);

    const listViewColDef: GridListViewColDef<IClause> = {
        field: "listColumn",
        renderCell: ClauseListViewCell,
    };

    return (
        <>
            <ClauseLibraryTableHeader />
            <Table
                loading={isFetching}
                columns={columns}
                rows={rows}
                autoSize={!isListView}
                autosizeOptions={!isListView ? autoSizeOptions : undefined}
                pageSizeOptions={[10, 25, 50]}
                paginationModel={paginationModel}
                rowCount={rowCount}
                onPaginationModelChange={setPaginationModel}
                paginationMode={"server"}
                filterMode={"server"}
                sortingMode={"server"}
                onFilterModelChange={handleFilterModelChange}
                onSortModelChange={handleFilterModelChange}
                showToolbar
                slots={{ toolbar: ClauseLibraryTableToolbar }}
                listView={isListView}
                listViewColumn={listViewColDef}
                density={isListView ? "comfortable" : "standard"}
            />
        </>
    );
};
